import { useEffect, useRef, useState } from "react";
import { Form, required, useLogin, useNotify, useTranslate } from "ra-core";
import type { FieldValues, SubmitHandler } from "react-hook-form";
import { Link, useLocation, useNavigate } from "react-router";

import { Button } from "@/components/ui/button";
import { TextInput } from "@/components/admin/text-input";
import { Notification } from "@/components/admin/notification";
import { Isotipo } from "../tema/Isotipo";
import { CampoContrasena } from "./CampoContrasena";
import { PanelMarca } from "./PanelMarca";

/**
 * Pantalla de login de Nevora Sales (copia adaptada de login/LoginPage.tsx de Atomic,
 * sin SSO). Panel de marca a la izquierda en escritorio; formulario a la derecha.
 */
export const PaginaLogin = (props: { redirectTo?: string }) => {
  const { redirectTo } = props;
  const [cargando, setCargando] = useState(false);
  const avisoRecuperacionMostrado = useRef(false);
  const location = useLocation();
  const navigate = useNavigate();
  const login = useLogin();
  const notify = useNotify();
  const translate = useTranslate();

  // Al volver de "¿has olvidado tu contraseña?" ra-supabase añade ?passwordRecoveryEmailSent=1
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (
      params.get("passwordRecoveryEmailSent") !== "1" ||
      avisoRecuperacionMostrado.current
    ) {
      return;
    }
    avisoRecuperacionMostrado.current = true;
    notify("crm.auth.recovery_email_sent", { type: "success" });
    params.delete("passwordRecoveryEmailSent");
    const resto = params.toString();
    navigate(
      { pathname: location.pathname, search: resto ? `?${resto}` : "" },
      { replace: true },
    );
  }, [location.pathname, location.search, navigate, notify]);

  const enviar: SubmitHandler<FieldValues> = (valores) => {
    setCargando(true);
    login(valores, redirectTo)
      .then(() => setCargando(false))
      .catch((error) => {
        setCargando(false);
        notify(
          typeof error === "string"
            ? error
            : !error?.message
              ? "ra.auth.sign_in_error"
              : error.message,
          {
            type: "error",
            messageArgs: {
              _:
                typeof error === "string"
                  ? error
                  : error?.message
                    ? error.message
                    : undefined,
            },
          },
        );
      });
  };

  return (
    <div className="min-h-screen flex">
      <div className="relative grid w-full lg:grid-cols-2">
        <PanelMarca />
        <div className="flex flex-col justify-center w-full p-6 lg:p-8">
          <div className="w-full space-y-8 lg:mx-auto lg:w-[360px]">
            <div className="flex items-center gap-3 lg:hidden">
              <Isotipo className="h-8" />
              <span className="font-marca uppercase tracking-[0.12em] text-lg font-medium">
                {translate("nevora.app.nombre")}
              </span>
            </div>
            <div>
              <p className="etiqueta mb-2">
                {translate("nevora.app.crm_interno")}
              </p>
              <h1 className="titulo-pagina">
                {translate("nevora.login.titulo")}
              </h1>
            </div>
            <Form className="space-y-6" onSubmit={enviar}>
              <TextInput
                label="ra.auth.email"
                source="email"
                type="email"
                autoComplete="email"
                validate={required()}
              />
              <CampoContrasena
                label="ra.auth.password"
                source="password"
                autoComplete="current-password"
                validate={required()}
              />
              <Button type="submit" className="w-full" disabled={cargando}>
                {translate("nevora.login.entrar")}
              </Button>
            </Form>
            <Link
              to="/forgot-password"
              className="block text-sm text-center hover:underline"
            >
              {translate("nevora.login.olvido")}
            </Link>
          </div>
        </div>
      </div>
      <Notification />
    </div>
  );
};
